/**
 * Company Verification (MCA / CIN via Surepass Corporate APIs) — Rudrik
 * ====================================================================
 *  Screen 1 — Search Company by name
 *  Screen 2 — Select the correct match from CIN list
 *  Screen 3 — Full MCA company profile (normalized + expandable raw JSON)
 */
import React, { useState } from "react";
import axios from "axios";
import {
  Building2, Search, Loader2, XCircle, ArrowLeft, CheckCircle2,
  Users, Landmark, FileText, Sparkles, Copy,
} from "lucide-react";
import { toast } from "sonner";

const API = window.location.origin;

const KV = ({ label, value }) => (
  <div style={{ display: "flex", gap: 12, padding: "8px 0",
                  borderBottom: "1px solid #12161d" }}>
    <div style={{ minWidth: 220, color: "#94a3b8", fontSize: 11,
                    textTransform: "uppercase", letterSpacing: 0.5,
                    fontWeight: 700 }}>{label}</div>
    <div style={{ flex: 1, color: "#f1f5f9", fontSize: 13, wordBreak: "break-word" }}>
      {value == null || value === "" || value === "Not available on record"
        ? <span style={{ color: "#64748b", fontStyle: "italic" }}>Not available</span>
        : String(value)}
    </div>
  </div>
);

const Section = ({ icon: Icon, title, children }) => (
  <div style={{ background: "#0d1117", border: "1px solid #1e2530",
                  borderRadius: 12, padding: 20, marginBottom: 14 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8,
                    color: "#f1f5f9", fontSize: 13, fontWeight: 800,
                    marginBottom: 12, textTransform: "uppercase",
                    letterSpacing: 0.6 }}>
      <Icon size={14} style={{ color: "#2563eb" }} /> {title}
    </div>
    {children}
  </div>
);

export default function CompanyVerifyPage() {
  const [step, setStep] = useState("search");    // search | select | details
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState([]);
  const [selectedCin, setSelectedCin] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const surface = (e, fallback) => {
    const raw = e?.response?.data?.detail;
    return (raw && typeof raw === "object" ? raw.message : raw)
           || e?.response?.data?.error || e?.message || fallback;
  };

  const doSearch = async () => {
    const name = query.trim();
    if (name.length < 3) return toast.error("Enter at least 3 characters");
    setLoading(true); setError(null); setMatches([]); setDetails(null);
    try {
      const { data } = await axios.post(`${API}/api/company/search`,
        { company_name: name },
        { withCredentials: true, timeout: 25000 });
      const rows = (data?.matches || []).filter(m => m.cin && m.cin !== "Not available on record");
      if (rows.length === 0) {
        toast("No matching companies found");
        setError(`No results for "${name}". Try a different or shorter query.`);
      }
      setMatches(rows);
      setStep(rows.length > 0 ? "select" : "search");
    } catch (e) {
      const msg = surface(e, "Company search failed");
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const fetchDetails = async (cin) => {
    setLoading(true); setError(null);
    setSelectedCin(cin);
    try {
      const { data } = await axios.post(`${API}/api/company/details`,
        { cin },
        { withCredentials: true, timeout: 35000 });
      setDetails(data);
      setStep("details");
    } catch (e) {
      const msg = surface(e, "Failed to fetch company details");
      setError(msg);
      toast.error(msg);
      setStep("select");
    } finally { setLoading(false); }
  };

  const copyJson = () => {
    if (!details?._raw_surepass) return;
    navigator.clipboard.writeText(JSON.stringify(details._raw_surepass, null, 2));
    toast.success("Raw JSON copied");
  };

  const reset = () => {
    setStep("search"); setQuery(""); setMatches([]);
    setSelectedCin(null); setDetails(null); setError(null);
  };

  return (
    <div data-testid="company-page" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center gap-3 mb-2">
        <Building2 style={{ width: 26, height: 26, color: "#2563eb" }} />
        <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Company Verification</h1>
        <span style={{ background: "rgba(34,197,94,0.15)", color: "#22c55e",
                         border: "1px solid #22c55e55", fontSize: 10,
                         padding: "3px 8px", borderRadius: 4, fontWeight: 700,
                         textTransform: "uppercase", letterSpacing: 0.5 }}>
          LIVE · MCA V3
        </span>
      </div>
      <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 18 }}>
        Search Indian companies by name, pick the right match, and fetch the full MCA record.
        Data source: Surepass Corporate APIs (Name → CIN → Company Details).
      </p>

      {/* ── STEP 1 — Search ─────────────────────────────────────── */}
      {step === "search" && (
        <div style={{ background: "#0d1117", border: "1px solid #1e2530",
                        borderRadius: 12, padding: 20 }}>
          <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700,
                          textTransform: "uppercase", letterSpacing: 0.6,
                          marginBottom: 6 }}>Company Name</div>
          <input data-testid="company-search-input"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === "Enter" && doSearch()}
            placeholder="e.g. Reliance Industries"
            autoFocus
            style={{ width: "100%", padding: "12px 14px", borderRadius: 10,
                       background: "#080c12", border: "1px solid #1e2530",
                       color: "#f1f5f9", fontSize: 14, marginBottom: 12 }} />
          <button onClick={doSearch} disabled={loading}
                  data-testid="company-search-btn"
                  style={{ padding: "10px 22px", borderRadius: 10,
                             background: loading ? "#1e2530"
                                        : "linear-gradient(135deg, #2563eb, #1d4ed8)",
                             color: "#fff", border: "none", fontSize: 13, fontWeight: 700,
                             cursor: loading ? "not-allowed" : "pointer",
                             display: "inline-flex", alignItems: "center", gap: 8 }}>
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={13} />}
            {loading ? "Searching…" : "Search Company"}
          </button>
        </div>
      )}

      {/* ── STEP 2 — Select from matches ────────────────────────── */}
      {step === "select" && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12,
                          marginBottom: 14 }}>
            <button onClick={reset} data-testid="company-back-search"
                    style={{ background: "transparent", border: "1px solid #1e2530",
                              color: "#94a3b8", borderRadius: 6, padding: "7px 12px",
                              fontSize: 11, fontWeight: 700, cursor: "pointer",
                              display: "inline-flex", alignItems: "center", gap: 6 }}>
              <ArrowLeft size={11} /> New search
            </button>
            <span style={{ color: "#94a3b8", fontSize: 12 }}>
              <b style={{ color: "#f1f5f9" }}>{matches.length}</b> match{matches.length === 1 ? "" : "es"} for
              {" "}<b style={{ color: "#f1f5f9" }}>"{query}"</b>
            </span>
          </div>
          <div data-testid="company-match-list"
               style={{ background: "#0d1117", border: "1px solid #1e2530",
                          borderRadius: 12, overflow: "hidden" }}>
            {matches.map((m, i) => (
              <div key={m.cin + i}
                   style={{ padding: "14px 18px",
                              borderBottom: i < matches.length - 1
                                             ? "1px solid #12161d" : "none",
                              display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: "#f1f5f9", fontSize: 14, fontWeight: 700 }}>
                    {m.company_name}
                  </div>
                  <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 2,
                                  fontFamily: "ui-monospace, monospace" }}>
                    {m.cin}
                    {m.company_type && m.company_type !== "Not available on record" &&
                     <> &middot; {m.company_type}</>}
                    {m.status && m.status !== "Not available on record" &&
                     <> &middot; {m.status}</>}
                  </div>
                </div>
                <button onClick={() => fetchDetails(m.cin)}
                        disabled={loading && selectedCin === m.cin}
                        data-testid={`company-view-${m.cin}`}
                        style={{ padding: "8px 14px", borderRadius: 8,
                                    background: (loading && selectedCin === m.cin)
                                              ? "#1e2530"
                                              : "linear-gradient(135deg, #2563eb, #1d4ed8)",
                                    color: "#fff", border: "none", fontSize: 11, fontWeight: 700,
                                    cursor: "pointer", display: "inline-flex",
                                    alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                  {loading && selectedCin === m.cin
                    ? <><Loader2 size={11} className="animate-spin" /> Loading…</>
                    : <><Sparkles size={11} /> View Full Details</>}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── STEP 3 — Full details ───────────────────────────────── */}
      {step === "details" && details && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12,
                          marginBottom: 14, flexWrap: "wrap" }}>
            <button onClick={() => setStep("select")} data-testid="company-back-select"
                    style={{ background: "transparent", border: "1px solid #1e2530",
                              color: "#94a3b8", borderRadius: 6, padding: "7px 12px",
                              fontSize: 11, fontWeight: 700, cursor: "pointer",
                              display: "inline-flex", alignItems: "center", gap: 6 }}>
              <ArrowLeft size={11} /> Back to matches
            </button>
            <button onClick={reset} data-testid="company-reset"
                    style={{ background: "transparent", border: "1px solid #1e2530",
                              color: "#94a3b8", borderRadius: 6, padding: "7px 12px",
                              fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
              New search
            </button>
          </div>

          <div style={{ background: "#0d1117",
                          border: "1px solid #22c55e55",
                          borderRadius: 12, padding: 20, marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CheckCircle2 size={20} style={{ color: "#22c55e" }} />
              <div style={{ flex: 1 }}>
                <div style={{ color: "#22c55e", fontSize: 18, fontWeight: 800 }}>
                  {details.company_name}
                </div>
                <div style={{ color: "#94a3b8", fontSize: 11,
                                fontFamily: "ui-monospace, monospace", marginTop: 3 }}>
                  CIN: {details.cin} &middot; {details.company_status || "Status unknown"}
                </div>
              </div>
            </div>
          </div>

          <Section icon={Landmark} title="Registration">
            <KV label="CIN" value={details.cin} />
            <KV label="Company name" value={details.company_name} />
            <KV label="Company status" value={details.company_status} />
            <KV label="Company type" value={details.company_type} />
            <KV label="Class of company" value={details.class_of_company} />
            <KV label="Category" value={details.company_category} />
            <KV label="Sub-category" value={details.company_sub_category} />
            <KV label="Listed status" value={details.listed_status} />
            <KV label="Date of incorporation" value={details.date_of_incorporation} />
            <KV label="Registration number" value={details.registration_number} />
            <KV label="ROC code" value={details.roc_code} />
          </Section>

          <Section icon={FileText} title="Capital & Compliance">
            <KV label="Authorised capital" value={details.authorised_capital} />
            <KV label="Paid-up capital" value={details.paid_up_capital} />
            <KV label="Principal business activity" value={details.principal_business_activity} />
            <KV label="Last AGM date" value={details.last_agm_date} />
            <KV label="Last balance sheet date" value={details.last_bs_date} />
            <KV label="Active compliance" value={details.active_compliance} />
            <KV label="Status under CIRP" value={details.status_under_cirp} />
            <KV label="Number of members" value={details.number_of_members} />
          </Section>

          <Section icon={Landmark} title="Address & Contact">
            <KV label="Registered address" value={details.registered_address} />
            <KV label="Address (other than R.O.)" value={details.address_other_than_ro} />
            <KV label="Email" value={details.email_id} />
          </Section>

          {(details.directors || []).length > 0 && (
            <Section icon={Users} title={`Directors (${details.directors.length})`}>
              {details.directors.map((d, i) => (
                <div key={i} style={{ padding: "10px 0",
                                        borderBottom: i < details.directors.length - 1
                                                       ? "1px solid #12161d" : "none" }}>
                  <div style={{ color: "#f1f5f9", fontSize: 13, fontWeight: 700 }}>
                    {d.name || <em>Unnamed director</em>}
                  </div>
                  <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 2,
                                  fontFamily: "ui-monospace, monospace" }}>
                    DIN {d.din || "—"}
                    {d.designation && d.designation !== "Not available on record" &&
                      <> &middot; {d.designation}</>}
                    {d.start_date && d.start_date !== "Not available on record" &&
                      <> &middot; Since {d.start_date}</>}
                  </div>
                </div>
              ))}
            </Section>
          )}

          {(details.charges || []).length > 0 && (
            <Section icon={Landmark} title={`Charges (${details.charges.length})`}>
              {details.charges.map((c, i) => (
                <div key={i} style={{ padding: "10px 0",
                                        borderBottom: i < details.charges.length - 1
                                                       ? "1px solid #12161d" : "none" }}>
                  <div style={{ color: "#f1f5f9", fontSize: 13, fontWeight: 700 }}>
                    {c.holder_name || "Unknown holder"} &middot; {c.amount || "—"}
                  </div>
                  <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 2 }}>
                    ID {c.charge_id || "—"} &middot; Created {c.date_of_creation || "—"}
                    &middot; Status: {c.status || "—"}
                  </div>
                </div>
              ))}
            </Section>
          )}

          {/* Raw JSON — expandable so no field is ever missed */}
          <div style={{ background: "#0d1117", border: "1px solid #1e2530",
                          borderRadius: 12, padding: 20 }}>
            <details>
              <summary style={{ cursor: "pointer", color: "#f59e0b", fontWeight: 800,
                                  fontSize: 12, textTransform: "uppercase",
                                  letterSpacing: 0.6, display: "flex",
                                  alignItems: "center", gap: 8 }}>
                <FileText size={12} /> Raw Surepass response (JSON)
                <button type="button" onClick={(e) => { e.preventDefault(); copyJson(); }}
                        data-testid="company-copy-raw"
                        style={{ marginLeft: "auto", background: "transparent",
                                    border: "1px solid #1e2530", color: "#94a3b8",
                                    borderRadius: 4, padding: "3px 8px",
                                    fontSize: 10, fontWeight: 700, cursor: "pointer",
                                    display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Copy size={10} /> Copy
                </button>
              </summary>
              <pre data-testid="company-raw-json"
                    style={{ marginTop: 12, padding: 12, borderRadius: 6,
                              background: "#080c12", border: "1px solid #1e2530",
                              color: "#94a3b8", fontSize: 10, overflowX: "auto",
                              maxHeight: 400 }}>
                {JSON.stringify(details._raw_surepass, null, 2)}
              </pre>
            </details>
            <div style={{ color: "#64748b", fontSize: 10, marginTop: 12,
                            paddingTop: 10, borderTop: "1px solid #12161d",
                            display: "flex", justifyContent: "space-between" }}>
              <span>Source: {details?._source?.provider || "MCA V3"}</span>
              {details._fetched_at &&
                <span>Fetched: {new Date(details._fetched_at).toLocaleString("en-IN")}</span>}
            </div>
          </div>
        </div>
      )}

      {/* Global error banner */}
      {error && step !== "details" && (
        <div data-testid="company-error"
             style={{ marginTop: 14, background: "#0d1117",
                        border: "1px solid #dc262655", borderRadius: 12,
                        padding: 16, display: "flex", gap: 10,
                        alignItems: "flex-start" }}>
          <XCircle size={18} style={{ color: "#dc2626", flexShrink: 0, marginTop: 1 }} />
          <div style={{ color: "#cbd5e1", fontSize: 12 }}>{error}</div>
        </div>
      )}
    </div>
  );
}
